"use client";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

function ShopBreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading || error || links.length === 0) return null;

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList className="text-muted-foreground font-default text-sm tracking-wide uppercase">
        {links.map((link, i) => (
          <BreadcrumbItem
            data-index={i}
            key={link.href}
            className="text-muted-foreground"
          >
            {i === lastIndex ? (
              <BreadcrumbPage className="text-foreground font-medium">
                {link.title}
              </BreadcrumbPage>
            ) : (
              <>
                <BreadcrumbLink
                  href={link.href}
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  {link.title}
                </BreadcrumbLink>
                <BreadcrumbSeparator className="text-muted-foreground/60" />
              </>
            )}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveTitleConsumer() {
  const { terms, loading, error } = useEcommerceTerms();

  if (loading) {
    return (
      <div data-section-id="3663" className="space-y-6">
        <div className="h-4 w-64 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-14 w-3/4 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-5 w-1/2 bg-muted/30 animate-pulse rounded-md" />
      </div>
    );
  }

  if (error || terms.length === 0) {
    return (
      <div className="space-y-4">
        <h1 className="font-default font-sans text-5xl md:text-7xl tracking-tight text-foreground">
          Our Collection
        </h1>
        <p className="text-muted-foreground max-w-2xl text-base md:text-lg leading-relaxed">
          Explore our meticulously curated selections of avant-garde apparel and timeless luxury staples.
        </p>
      </div>
    );
  }

  const term = terms[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="h-px w-12 bg-primary" />
        <span className="font-default text-xs tracking-widest uppercase text-primary">
          {term.taxonomy === "product_tag" ? "Style Tag" : "Category"}
        </span>
      </div>
      <h1 className="font-default font-sans text-5xl md:text-7xl lg:text-8xl tracking-tight text-foreground leading-[1.05]">
        {term.name}
      </h1>
      {term.description && (
        <p
          className="text-muted-foreground max-w-2xl text-base md:text-lg leading-relaxed"
          dangerouslySetInnerHTML={{ __html: term.description }}
        />
      )}
      <div className="flex items-center gap-2 pt-2">
        <span className="font-default text-xs uppercase tracking-widest text-muted-foreground">
          DRIP DISTRICT — Premium Quality Apparel
        </span>
      </div>
    </div>
  );
}

export default function () {
  const current_wp_term_query_params =
    (window as any).wvcClient.getCurrentWPTermQueryParams();
  const wp_term_query = new WP_Term_Query(current_wp_term_query_params);

  return (
    <section
      id="archive-title-section"
      className="bg-background text-foreground relative overflow-hidden border-b border-border"
    >
      {}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      {}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 right-0 h-full w-1/3 opacity-30"
        style={{
          background:
            "radial-gradient(circle at 100% 50%, var(--primary) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-28">
        {}
        <div className="mb-10 md:mb-14">
          <ShopBreadcrumbsProvider>
            <ShopBreadcrumbsConsumer />
          </ShopBreadcrumbsProvider>
        </div>

        {}
        <EcommerceTermsProvider wp_term_query={wp_term_query}>
          <ArchiveTitleConsumer />
        </EcommerceTermsProvider>

        {}
        <div className="mt-12 md:mt-16 flex items-center gap-4">
          <div className="h-px flex-1 bg-border" />
          <span className="font-default text-xs uppercase tracking-widest text-muted-foreground">
            Drip District Est. 2024
          </span>
          <div className="h-px w-16 bg-primary" />
        </div>
      </div>
    </section>
  );
}